import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Quote, Trash2, Pencil } from 'lucide-react';
import {
  FACT_KEYS,
  FACT_LABELS,
  CONFIDENCE_LEVELS,
  CONFIDENCE_LABELS,
  CONFIDENCE_DESCRIPTIONS,
  isConfidenceLevel,
  isFactKey,
  type ConfidenceLevel,
  type FactKey,
} from '@genealogy-app/shared';
import apiClient from '@/api/client';
import { QUERY_KEYS } from '@/api/queryKeys';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/store/useAuthStore';
import { FactConfidenceBadge } from '../FactConfidence';
import type { PersonDetail } from '@/api/persons.api';

// ─── Belege je Angabe ────────────────────────────────────────────────────────
//
// Eine Quelle an der Person sagt nur, DASS es einen Beleg gibt. Hier wird
// festgehalten, WOFÜR: „Geburtsdatum – Kirchenbuch, S. 3 – gesichert."

const INPUT_CLASS =
  'w-full rounded border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400';

type Citation = NonNullable<PersonDetail['factCitations']>[number];

function CitationForm({
  person,
  citation,
  onDone,
}: {
  person: PersonDetail;
  citation?: Citation;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!citation;
  const [fact, setFact] = useState<FactKey>(
    citation && isFactKey(citation.fact) ? citation.fact : 'birthDate',
  );
  const [confidence, setConfidence] = useState<ConfidenceLevel>(
    citation && isConfidenceLevel(citation.confidence) ? citation.confidence : 'secondary',
  );
  const [sourceId, setSourceId] = useState(citation?.sourceId ?? '');
  const [locator, setLocator] = useState(citation?.locator ?? '');

  const save = useMutation({
    mutationFn: async () => {
      const dto = {
        fact,
        confidence,
        sourceId: sourceId || null,
        locator: locator.trim() || null,
      };
      if (isEdit) {
        await apiClient.put(`/persons/${person.id}/facts/${citation!.id}`, dto);
      } else {
        await apiClient.post(`/persons/${person.id}/facts`, dto);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(person.id) });
      toast.success(isEdit ? 'Beleg aktualisiert.' : 'Beleg hinzugefügt.');
      onDone();
    },
    onError: () => toast.error('Beleg konnte nicht gespeichert werden.'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/30 p-3"
    >
      <div>
        <label htmlFor="fc-fact" className="mb-1 block text-xs font-medium text-slate-600">
          Welche Angabe?
        </label>
        <select
          id="fc-fact"
          value={fact}
          onChange={(e) => setFact(e.target.value as FactKey)}
          className={INPUT_CLASS}
        >
          {FACT_KEYS.map((k) => (
            <option key={k} value={k}>
              {FACT_LABELS[k]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="fc-confidence" className="mb-1 block text-xs font-medium text-slate-600">
          Sicherheitsgrad
        </label>
        <select
          id="fc-confidence"
          value={confidence}
          onChange={(e) => setConfidence(e.target.value as ConfidenceLevel)}
          className={INPUT_CLASS}
        >
          {CONFIDENCE_LEVELS.map((c) => (
            <option key={c} value={c}>
              {CONFIDENCE_LABELS[c]}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] leading-snug text-slate-500">
          {CONFIDENCE_DESCRIPTIONS[confidence]}
        </p>
      </div>

      <div>
        <label htmlFor="fc-source" className="mb-1 block text-xs font-medium text-slate-600">
          Quelle
        </label>
        <select
          id="fc-source"
          value={sourceId}
          onChange={(e) => setSourceId(e.target.value)}
          className={INPUT_CLASS}
        >
          <option value="">— ohne Quelle (reine Einschätzung) —</option>
          {person.sources.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title ?? 'Ohne Titel'}
            </option>
          ))}
        </select>
        {person.sources.length === 0 && (
          <p className="mt-1 text-[11px] text-slate-400">
            Noch keine Quellen erfasst – der Beleg bleibt dann eine Einschätzung.
          </p>
        )}
      </div>

      <div>
        <label htmlFor="fc-locator" className="mb-1 block text-xs font-medium text-slate-600">
          Fundort in der Quelle
        </label>
        <input
          id="fc-locator"
          type="text"
          value={locator}
          onChange={(e) => setLocator(e.target.value)}
          placeholder="z. B. S. 3, Eintrag Nr. 7"
          className={INPUT_CLASS}
        />
      </div>

      <div className="flex gap-2 pt-0.5">
        <button
          type="submit"
          disabled={save.isPending}
          className="flex-1 rounded bg-blue-600 px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 sm:flex-none"
        >
          {save.isPending ? 'Speichern…' : 'Speichern'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

export function FactCitationsSection({ person }: { person: PersonDetail }) {
  const canManage = useAuthStore((s) => s.hasPermission('persons.manage'));
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const citations = person.factCitations ?? [];

  const remove = useMutation({
    mutationFn: async (citationId: string) => {
      await apiClient.delete(`/persons/${person.id}/facts/${citationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(person.id) });
      toast.success('Beleg entfernt.');
    },
    onError: () => toast.error('Beleg konnte nicht entfernt werden.'),
  });

  const handleRemove = useCallback(
    async (c: Citation) => {
      const label = isFactKey(c.fact) ? FACT_LABELS[c.fact] : c.fact;
      const ok = await confirm({
        title: 'Beleg entfernen?',
        message: (
          <>
            Beleg für <strong>{label}</strong> wirklich entfernen? Die Quelle selbst bleibt
            erhalten.
          </>
        ),
        confirmLabel: 'Entfernen',
        variant: 'danger',
      });
      if (ok) remove.mutate(c.id);
    },
    [confirm, remove],
  );

  return (
    <section className="space-y-2 border-t border-slate-200 pt-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
          <Quote className="h-3.5 w-3.5" aria-hidden="true" />
          Belege je Angabe
        </h4>
        {canManage && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Beleg
          </button>
        )}
      </div>

      <p className="text-[11px] leading-snug text-slate-500">
        Hält fest, woher eine einzelne Angabe stammt – und wie belastbar sie ist.
      </p>

      {showForm && (
        <CitationForm person={person} onDone={() => setShowForm(false)} />
      )}

      {citations.length === 0 && !showForm && (
        <p className="py-3 text-center text-sm text-slate-400">Noch keine Belege je Angabe.</p>
      )}

      {citations.map((c) =>
        editingId === c.id ? (
          <CitationForm
            key={c.id}
            person={person}
            citation={c}
            onDone={() => setEditingId(null)}
          />
        ) : (
          <div
            key={c.id}
            className="group flex items-start justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/50 p-2.5"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-slate-800">
                  {isFactKey(c.fact) ? FACT_LABELS[c.fact] : c.fact}
                </span>
                {isConfidenceLevel(c.confidence) && (
                  <FactConfidenceBadge level={c.confidence} />
                )}
              </div>
              <p className="mt-0.5 truncate text-xs text-slate-500">
                {c.source ? (c.source.title ?? 'Ohne Titel') : 'ohne Quelle'}
                {c.locator ? ` · ${c.locator}` : ''}
              </p>
            </div>

            {/* Auf Touch-Geräten immer sichtbar – dort gibt es kein Hover. */}
            {canManage && (
              <div className="flex shrink-0 items-center gap-0.5 transition-opacity lg:opacity-0 lg:group-hover:opacity-100 lg:focus-within:opacity-100">
                <button
                  onClick={() => setEditingId(c.id)}
                  className="rounded p-1 text-slate-300 hover:bg-blue-50 hover:text-blue-600"
                  aria-label="Beleg bearbeiten"
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  onClick={() => handleRemove(c)}
                  className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500"
                  aria-label="Beleg entfernen"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        ),
      )}
    </section>
  );
}
